import { addDays, differenceInCalendarDays, endOfMonth, endOfYear, format } from 'date-fns';

export interface DailyMessageStat {
  date: string;
  message_count: number;
}

export interface MessagesDataset {
  labels: string[];
  values: number[];
}

type BucketSize = 'day' | 'week' | 'month' | 'year';

export interface DateBucket {
  label: string;
  start: Date;
  end: Date;
}

const DATE_FORMAT = 'yyyy-MM-dd';

export const buildMessagesDataset = (
  dailyStats: DailyMessageStat[],
  startDate: string,
  endDate: string
): MessagesDataset => {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  if (!start || !end || start > end) {
    return { labels: [], values: [] };
  }

  const normalizedStats = normalizeDailyStats(dailyStats);
  const buckets = createDateBuckets(start, end);

  const labels = buckets.map((bucket) => bucket.label);
  const values = buckets.map((bucket) =>
    sumStatsInRange(normalizedStats, bucket.start, bucket.end)
  );

  return { labels, values };
};

const parseDate = (value: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeDailyStats = (dailyStats: DailyMessageStat[]) => {
  const statsMap = new Map<string, number>();
  dailyStats.forEach((stat) => {
    if (!stat?.date) return;
    const parsed = parseDate(stat.date);
    if (!parsed) return;
    const dateKey = format(parsed, DATE_FORMAT);
    statsMap.set(dateKey, stat?.message_count ?? 0);
  });
  return statsMap;
};

const sumStatsInRange = (
  statsMap: Map<string, number>,
  start: Date,
  end: Date
): number => {
  let current = start;
  let total = 0;

  while (current <= end) {
    const key = format(current, DATE_FORMAT);
    total += statsMap.get(key) ?? 0;
    current = addDays(current, 1);
  }

  return total;
};

export const createDateBuckets = (startDate: Date, endDate: Date): DateBucket[] => {
  const totalDays = differenceInCalendarDays(endDate, startDate) + 1;
  const bucketSize = determineBucketSize(totalDays);
  const buckets: DateBucket[] = [];

  let current = startDate;
  while (current <= endDate) {
    const bucketStart = current;
    let bucketEnd = bucketStart;
    let label = format(bucketStart, 'MMM d');

    if (bucketSize === 'day') {
      bucketEnd = bucketStart;
      label = format(bucketStart, 'MMM d');
      current = addDays(bucketStart, 1);
    } else if (bucketSize === 'week') {
      bucketEnd = minDate(addDays(bucketStart, 6), endDate);
      label = `Week of ${format(bucketStart, 'MMM d')}`;
      current = addDays(bucketEnd, 1);
    } else if (bucketSize === 'month') {
      bucketEnd = minDate(endOfMonth(bucketStart), endDate);
      label = format(bucketStart, 'MMM yyyy');
      current = addDays(bucketEnd, 1);
    } else {
      bucketEnd = minDate(endOfYear(bucketStart), endDate);
      label = format(bucketStart, 'yyyy');
      current = addDays(bucketEnd, 1);
    }

    buckets.push({ label, start: bucketStart, end: bucketEnd });
  }

  return buckets;
};

const determineBucketSize = (totalDays: number): BucketSize => {
  if (totalDays <= 30) return 'day';
  if (totalDays <= 30 * 4) return 'week';
  if (totalDays <= 30 * 12) return 'month';
  return 'year';
};

const minDate = (left: Date, right: Date) => (left < right ? left : right);
